import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../config/constants";
import api from "../utils/api";
import { getReadableApiError } from "../utils/readableApiResponse";
import { loginSuccess, logout } from "./authSlice";

const DEFAULT_NOTIFICATION_LIMIT = 50;

const getInitialNotificationsState = () => ({
    items: [],
    unreadCount: 0,
    nextCursor: null,
    feedUnreadOnly: false,

    feedStatus: "idle",
    feedError: null,

    unreadCountStatus: "idle",
    unreadCountError: null,

    markReadStatusById: {},
    markReadErrorById: {},

    markAllReadStatus: "idle",
    markAllReadError: null,
});

const getNotificationId = (notification) =>
    String(notification?.id || "").trim();

const normalizeUnreadCount = (value) => {
    const count = Number(value);

    if (!Number.isFinite(count) || count < 0) return 0;

    return Math.trunc(count);
};

const normalizeMarkedCount = (value) => {
    const count = Number(value);

    if (!Number.isFinite(count) || count < 0) return 0;

    return Math.trunc(count);
};

const normalizeNextCursor = (value) => {
    if (value === null || value === undefined) return null;

    const cursor = String(value).trim();

    return cursor || null;
};

const normalizeNotification = (notification) => {
    if (!notification || typeof notification !== "object") return null;

    const id = getNotificationId(notification);

    if (!id) return null;

    return {
        ...notification,
        id,
        type: String(notification.type || ""),
        title: String(notification.title || ""),
        message: String(notification.message || ""),
        meta:
            notification.meta &&
            typeof notification.meta === "object" &&
            !Array.isArray(notification.meta)
                ? notification.meta
                : {},
        is_read: notification.is_read === true,
        created_at: String(notification.created_at || ""),
    };
};

const normalizeNotificationItems = (items) => {
    if (!Array.isArray(items)) return [];

    const uniqueItems = new Map();

    items.forEach((item) => {
        const notification = normalizeNotification(item);

        if (!notification) return;

        uniqueItems.set(notification.id, notification);
    });

    return Array.from(uniqueItems.values());
};

const mergeNotificationItems = (existingItems, incomingItems) => {
    const mergedItems = Array.isArray(existingItems) ? [...existingItems] : [];
    const existingIds = new Set(mergedItems.map(getNotificationId));

    incomingItems.forEach((item) => {
        if (!item?.id || existingIds.has(item.id)) return;

        mergedItems.push(item);
        existingIds.add(item.id);
    });

    return mergedItems;
};

const normalizeNotificationsResponse = (data) => ({
    items: normalizeNotificationItems(data?.items),
    unreadCount: normalizeUnreadCount(data?.unread_count),
    nextCursor: normalizeNextCursor(data?.next_cursor),
});

const normalizeMutationResponse = (data) => ({
    success: data?.success === true,
    markedCount: normalizeMarkedCount(data?.marked_count),
});

const getNotificationRequestErrorPayload = (error, fallbackMessage) => ({
    message: getReadableApiError(error, fallbackMessage),
    status: error.response?.status || null,
});

export const fetchNotifications = createAsyncThunk(
    "notifications/fetchNotifications",
    async (
        { cursor = null, limit = DEFAULT_NOTIFICATION_LIMIT, unreadOnly = false } = {},
        thunkAPI,
    ) => {
        try {
            const response = await api.get(API_ENDPOINTS.NOTIFICATIONS, {
                params: {
                    cursor: cursor || undefined,
                    limit,
                    unread_only: unreadOnly,
                },
            });

            return {
                ...normalizeNotificationsResponse(response.data),
                cursor: cursor || null,
                unreadOnly,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch notifications."),
            );
        }
    },
    {
        condition: (_, { getState }) => {
            const { auth, notifications } = getState();

            return (
                Boolean(auth?.isAuthenticated && auth?.token) &&
                notifications?.feedStatus !== "loading"
            );
        },
    },
);

export const fetchUnreadNotificationCount = createAsyncThunk(
    "notifications/fetchUnreadNotificationCount",
    async (_, thunkAPI) => {
        try {
            const response = await api.get(
                API_ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT,
            );

            return {
                count: normalizeUnreadCount(response.data?.count),
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to fetch unread notification count.",
                ),
            );
        }
    },
    {
        condition: (_, { getState }) => {
            const { auth, notifications } = getState();

            return (
                Boolean(auth?.isAuthenticated && auth?.token) &&
                notifications?.unreadCountStatus !== "loading"
            );
        },
    },
);

export const markNotificationRead = createAsyncThunk(
    "notifications/markNotificationRead",
    async (notificationId, thunkAPI) => {
        const id = String(notificationId || "").trim();

        if (!id) {
            return thunkAPI.rejectWithValue("Notification ID is required.");
        }

        try {
            const response = await api.post(
                API_ENDPOINTS.NOTIFICATION_READ(id),
                null,
            );

            return {
                notificationId: id,
                ...normalizeMutationResponse(response.data),
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getNotificationRequestErrorPayload(
                    error,
                    "Failed to mark notification as read.",
                ),
            );
        }
    },
    {
        condition: (notificationId, { getState }) => {
            const id = String(notificationId || "").trim();
            const { auth, notifications } = getState();

            if (!auth?.isAuthenticated || !auth?.token || !id) return false;
            if (notifications?.markReadStatusById?.[id] === "loading") {
                return false;
            }

            const notification = notifications?.items?.find(
                (item) => item?.id === id,
            );

            return notification?.is_read !== true;
        },
    },
);

export const markAllNotificationsRead = createAsyncThunk(
    "notifications/markAllNotificationsRead",
    async (_, thunkAPI) => {
        try {
            const response = await api.post(
                API_ENDPOINTS.NOTIFICATIONS_MARK_ALL_READ,
                null,
            );

            return normalizeMutationResponse(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to mark notifications as read.",
                ),
            );
        }
    },
    {
        condition: (_, { getState }) => {
            const { auth, notifications } = getState();

            return (
                Boolean(auth?.isAuthenticated && auth?.token) &&
                notifications?.markAllReadStatus !== "loading" &&
                Number(notifications?.unreadCount) > 0
            );
        },
    },
);

const notificationsSlice = createSlice({
    name: "notifications",
    initialState: getInitialNotificationsState(),
    reducers: {
        clearNotificationsFeedError: (state) => {
            state.feedError = null;
        },
        clearUnreadNotificationCountError: (state) => {
            state.unreadCountError = null;
        },
        clearMarkAllNotificationsReadState: (state) => {
            state.markAllReadStatus = "idle";
            state.markAllReadError = null;
        },
        clearMarkNotificationReadState: (state, action) => {
            const notificationId = String(action.payload || "").trim();

            if (!notificationId) return;

            delete state.markReadStatusById[notificationId];
            delete state.markReadErrorById[notificationId];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.feedStatus = "loading";
                state.feedError = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                const { cursor, items, unreadCount, nextCursor, unreadOnly } =
                    action.payload;

                state.items = cursor
                    ? mergeNotificationItems(state.items, items)
                    : items;
                state.unreadCount = unreadCount;
                state.nextCursor = nextCursor;
                state.feedUnreadOnly = unreadOnly === true;
                state.feedStatus = "succeeded";
                state.feedError = null;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.feedStatus = "failed";
                state.feedError =
                    action.payload || "Failed to fetch notifications.";
            })
            .addCase(fetchUnreadNotificationCount.pending, (state) => {
                state.unreadCountStatus = "loading";
                state.unreadCountError = null;
            })
            .addCase(
                fetchUnreadNotificationCount.fulfilled,
                (state, action) => {
                    state.unreadCount = action.payload.count;
                    state.unreadCountStatus = "succeeded";
                    state.unreadCountError = null;
                },
            )
            .addCase(fetchUnreadNotificationCount.rejected, (state, action) => {
                state.unreadCountStatus = "failed";
                state.unreadCountError =
                    action.payload ||
                    "Failed to fetch unread notification count.";
            })
            .addCase(markNotificationRead.pending, (state, action) => {
                const notificationId = String(action.meta.arg || "").trim();

                if (!notificationId) return;

                state.markReadStatusById[notificationId] = "loading";
                delete state.markReadErrorById[notificationId];
            })
            .addCase(markNotificationRead.fulfilled, (state, action) => {
                const { notificationId, markedCount } = action.payload;
                const notification = state.items.find(
                    (item) => item.id === notificationId,
                );

                if (notification && state.feedUnreadOnly) {
                    state.items = state.items.filter(
                        (item) => item.id !== notificationId,
                    );
                } else if (notification) {
                    notification.is_read = true;
                }

                if (markedCount === 1) {
                    state.unreadCount = Math.max(0, state.unreadCount - 1);
                }

                state.markReadStatusById[notificationId] = "succeeded";
                delete state.markReadErrorById[notificationId];
            })
            .addCase(markNotificationRead.rejected, (state, action) => {
                const notificationId = String(action.meta.arg || "").trim();
                const errorPayload = action.payload;
                const errorStatus =
                    typeof errorPayload === "object" && errorPayload !== null
                        ? errorPayload.status
                        : null;
                const errorMessage =
                    typeof errorPayload === "object" && errorPayload !== null
                        ? errorPayload.message
                        : errorPayload;

                if (!notificationId) return;

                if (errorStatus === 404) {
                    const staleNotification = state.items.find(
                        (item) => item.id === notificationId,
                    );

                    state.items = state.items.filter(
                        (item) => item.id !== notificationId,
                    );

                    if (staleNotification?.is_read !== true) {
                        state.unreadCount = Math.max(
                            0,
                            state.unreadCount - 1,
                        );
                    }

                    delete state.markReadStatusById[notificationId];
                    delete state.markReadErrorById[notificationId];
                    return;
                }

                state.markReadStatusById[notificationId] = "failed";
                state.markReadErrorById[notificationId] =
                    errorMessage ||
                    "Failed to mark notification as read.";
            })
            .addCase(markAllNotificationsRead.pending, (state) => {
                state.markAllReadStatus = "loading";
                state.markAllReadError = null;
            })
            .addCase(markAllNotificationsRead.fulfilled, (state, action) => {
                const markedCount = action.payload.markedCount;

                state.items = state.feedUnreadOnly
                    ? []
                    : state.items.map((notification) => ({
                          ...notification,
                          is_read: true,
                      }));
                state.unreadCount = Math.max(
                    0,
                    state.unreadCount - markedCount,
                );
                state.markAllReadStatus = "succeeded";
                state.markAllReadError = null;
            })
            .addCase(markAllNotificationsRead.rejected, (state, action) => {
                state.markAllReadStatus = "failed";
                state.markAllReadError =
                    action.payload ||
                    "Failed to mark notifications as read.";
            })
            .addCase(loginSuccess, () => getInitialNotificationsState())
            .addCase(logout, () => getInitialNotificationsState());
    },
});

export const {
    clearNotificationsFeedError,
    clearUnreadNotificationCountError,
    clearMarkAllNotificationsReadState,
    clearMarkNotificationReadState,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
